<template>
  <div class="client-questions-tab">
    <v-card class="mb-6">
      <v-card-title class="primary--text">
        <v-icon left color="primary">mdi-account-voice</v-icon>
        Client Questions
      </v-card-title>
      <v-card-text>
        <v-simple-table>
          <template #default>
            <thead>
              <tr>
                <th class="text-left">Question</th>
                <th class="text-left">Candidate answer</th>
                <th class="text-left">Link</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in normalizedRows" :key="row.key">
                <td
                  :class="{ 'cq-question--seekable': isSeekableRow(row) }"
                  @click="onQuestionCellClick(row)"
                >
                  {{ row.question || '—' }}
                </td>
                <td>{{ row.answer || '—' }}</td>
                <td>
                  <v-btn
                    v-if="
                      row.timestamp_seconds != null &&
                      Number.isFinite(Number(row.timestamp_seconds))
                    "
                    text
                    small
                    color="primary"
                    @click="onJump(row.timestamp_seconds)"
                  >
                    {{ formatTime(row.timestamp_seconds) }}
                  </v-btn>
                  <span v-else class="grey--text">N/A</span>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

/**
 * Client questions block of a full_interview document: own stage result
 * (results.client_questions, V10 split), legacy location inside results.cultural_fit, or the
 * flattened top-level key of the document. Null when the document carries none.
 */
export function resolveClientQuestions(fullInterviewResult) {
  const results = fullInterviewResult?.results;
  return (
    results?.client_questions ||
    results?.cultural_fit?.client_questions ||
    fullInterviewResult?.client_questions ||
    null
  );
}

/**
 * Whether the Client questions tab has something to show: a client-questions block, else (run
 * with a known engine selection) whether the engine executed, else the legacy cultural-fit
 * readiness the tab used to share.
 * @param {{ fullInterviewResult: object|null, enginesRun: { known: boolean, executed: string[] },
 *   culturalFitReady: boolean }} params
 */
export function isClientQuestionsAvailable({ fullInterviewResult, enginesRun, culturalFitReady }) {
  if (resolveClientQuestions(fullInterviewResult)) return true;
  if (enginesRun?.known) return (enginesRun.executed || []).includes('client_questions');
  return Boolean(culturalFitReady);
}

export default {
  name: 'ClientQuestionsTab',

  props: {
    // Store key (composite analysisKey or numeric id) used for cross-tab video seek signals.
    interviewId: {
      type: [Number, String],
      required: true,
    },
    beliefsAnalysis: {
      type: Object,
      default: () => null,
    },
  },

  computed: {
    fullInterviewResult() {
      return this.beliefsAnalysis?.fullInterviewResult || null;
    },
    culturalFit() {
      return this.fullInterviewResult?.results?.cultural_fit || null;
    },
    clientQuestions() {
      return resolveClientQuestions(this.fullInterviewResult);
    },
    rawRows() {
      const data = this.clientQuestions?.data;
      if (!Array.isArray(data)) return [];
      return data;
    },
    normalizedRows() {
      const out = [];
      this.rawRows.forEach((row, idx) => {
        if (Array.isArray(row)) {
          const qa = row[0] || {};
          out.push({
            key: `cq-${idx}-0`,
            question: qa.question || '',
            answer: qa.answer || qa.candidate_answer || qa.candidateAnswer || '',
            ai_summary: qa.ai_summary || qa.aiSummary || '',
            timestamp_seconds:
              qa.timestamp_seconds != null
                ? Number(qa.timestamp_seconds)
                : qa.timestampSeconds != null
                  ? Number(qa.timestampSeconds)
                  : null,
          });
          return;
        }
        out.push({
          key: `cq-${idx}`,
          question: row.question || '',
          answer: row.answer || row.candidate_answer || row.candidateAnswer || '',
          ai_summary: row.ai_summary || row.aiSummary || '',
          timestamp_seconds:
            row.timestamp_seconds != null
              ? Number(row.timestamp_seconds)
              : row.timestampSeconds != null
                ? Number(row.timestampSeconds)
                : null,
        });
      });
      // Emit exactly the client questions that exist (no pad/truncate to 3).
      return out;
    },
  },

  methods: {
    ...mapActions('analysisData', ['requestInterviewVideoSeek']),

    formatTime(seconds) {
      const s = Number(seconds);
      if (!Number.isFinite(s) || s < 0) return 'N/A';
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    },

    onJump(seconds) {
      this.requestInterviewVideoSeek({ analysisId: this.interviewId, seconds });
    },

    isSeekableRow(row) {
      const s = row?.timestamp_seconds;
      return s != null && Number.isFinite(Number(s));
    },

    onQuestionCellClick(row) {
      if (!this.isSeekableRow(row)) return;
      this.onJump(Number(row.timestamp_seconds));
    },
  },
};
</script>

<style scoped>
.client-questions-tab {
  padding: 16px 0;
}

.cq-question--seekable {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: rgba(0, 0, 0, 0.38);
  text-underline-offset: 2px;
}

.cq-question--seekable:hover {
  color: var(--v-primary-base, #1976d2);
  text-decoration-color: currentColor;
}
</style>
